import Image from 'next/image';
import { useMemo } from 'react';
import { NetworkData, NETWORKS } from 'src/common/types/network';

import { KeyValueRow, KeyValueTable, Text } from '@/design-system/components/ui';
import useGetChainInfo from '@/hooks/useGetChainInfo';

import { Trigger } from '../../domain';
import { AddressChip } from './components/AddressChip';
import { CurrencyDisplay } from './components/CurrencyDisplay';

interface Props {
  trigger: Trigger;
}

export function MorphoTriggerFullDetails({ trigger }: Props) {
  const { getChainIconUrl } = useGetChainInfo();

  const vaultAddress = useMemo(() => {
    if (!trigger.signature) return '';
    const match = trigger.signature.match(/@([^@]+)$/);
    return match ? match[1] : '';
  }, [trigger.signature]);

  // Get network info from signature (e.g., "Ethereum" in "SubgraphBalance-Morpho-User-Daily-Fees-Ethereum@...")
  const networkInfo = useMemo(() => {
    if (!trigger.signature) return null;
    const match = trigger.signature.match(/Fees-(\w+)@/);
    if (!match) return null;

    const networkName = match[1].toLowerCase();
    return NETWORKS.find((network: NetworkData) => network.name.toLowerCase() === networkName);
  }, [trigger.signature]);

  const networkDisplay = useMemo(() => {
    if (!networkInfo) return '-';
    const chainIconUrl = getChainIconUrl(networkInfo.chainId);

    return (
      <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
        {chainIconUrl && <Image src={chainIconUrl} alt={networkInfo.name} width={20} height={20} />}
        <span style={{ textTransform: 'capitalize' }}>{networkInfo.name.replace(/-/g, ' ')}</span>
      </div>
    );
  }, [networkInfo, getChainIconUrl]);

  const contractDisplay = useMemo(() => {
    if (!vaultAddress || !networkInfo) return '-';

    return (
      <div className="tw:flex tw:flex-row tw:gap-2 tw:items-center">
        <Text as="span" variant="body-sm" color="muted">
          Snapshots of token holders for contract
        </Text>
        <AddressChip address={vaultAddress} chainId={networkInfo.chainId} />
      </div>
    );
  }, [vaultAddress, networkInfo]);

  const volumeCurrencyDisplay = useMemo(() => {
    if (!trigger.currencyVolumeExpression) return '-';
    const chainId = networkInfo?.chainId ?? 1; // Default to mainnet if not found

    return <CurrencyDisplay value={trigger.currencyVolumeExpression} chainId={chainId} />;
  }, [trigger.currencyVolumeExpression, networkInfo]);

  return (
    <KeyValueTable>
      <KeyValueRow label="Name" value={trigger.name} />
      <KeyValueRow label="Description" value={trigger.description} />
      <KeyValueRow label="Executed with" value={contractDisplay} />
      <KeyValueRow label="Network" value={networkDisplay} />
      <KeyValueRow label="Volume currency" value={volumeCurrencyDisplay} />
      <KeyValueRow label="Argument conditions" value={trigger.conditionExpression ?? '-'} />
    </KeyValueTable>
  );
}
